import React from 'react';
import styled from 'styled-components';

const ActionButton = styled.button`
  padding: 6px 12px;
  margin: 2px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  background-color: #005eb8;
  color: white;
  &:hover { background-color: #004a94; }
  &:disabled { background-color: #adb5bd; cursor: not-allowed; }
  &.view { background-color: #6f42c1; } /* Purple for viewing the final letter */
  &.view-file { background-color: #20c997; } /* Teal for viewing supporting files */
  &.complete { background-color: #28a745; }
  &.edit { background-color: #005eb8; }
  &.save { background-color: #17a2b8; } /* Info blue for saving */
  &.reopen { background-color: #f0ad4e; }
  &.regenerate { background-color: #fd7e14; }
  &.reject { background-color: #dc3545; } /* Danger red for rejecting */
`;

const EditorWrapper = styled.div`
  padding: 15px;
  background-color: #f0f4f8;
  border-radius: 4px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
`;

const OrderRow = ({ order, activeTab, editingOrderId, completingOrderId, reopeningOrderId, regeneratingOrderId, draft, transcript, handlers, isAiProcessing }) => {
    const { orderDetails, uploads, questionnaireAnswers, questionnaireFiles, userName, userDob, userAddress, userPhone } = order;
    const isEditing = editingOrderId === orderDetails.orderId;
    const isCompleting = completingOrderId === orderDetails.orderId;
    const isReopening = reopeningOrderId === orderDetails.orderId;
    const isRegenerating = regeneratingOrderId === orderDetails.orderId;

    const addressString = userAddress ? [userAddress.line1, userAddress.city, userAddress.postal_code].filter(Boolean).join(', ') : 'N/A';

    const renderFileButton = (file, label) => {
        if (!file || !file.url) return <span>N/A</span>;
        return ( // Added 'view-file' class for specific styling
            <ActionButton className="view-file" onClick={() => handlers.onViewFile({ type: 'file', url: file.url, title: label, mimetype: file.mimetype })}>
                View {label}
            </ActionButton>
        );
    };

    return (
        <>
            <tr>
                <td>{orderDetails.orderId}</td>
                <td>{userName}</td>
                <td>{userDob ? new Date(userDob).toLocaleDateString('en-GB') : 'N/A'}</td>
                <td>{addressString}</td>
                <td>{userPhone || 'N/A'}</td>
                <td>{new Date(orderDetails.date).toLocaleDateString('en-GB')}</td>

                {/* ID Column */}
                <td>{renderFileButton(uploads?.id, 'ID')}</td>

                {/* Photo Evidence Column */}
                <td>{renderFileButton(uploads?.evidence, 'Evidence')}</td>

                {/* SCR Column */}
                <td>{renderFileButton(uploads?.scr, 'SCR')}</td>

                {/* Questionnaire Column */}
                <td>{(questionnaireAnswers || questionnaireFiles) ? <ActionButton className="view-file" onClick={() => handlers.onViewQuestionnaire(orderDetails.orderId, questionnaireAnswers, questionnaireFiles)}>View Q</ActionButton> : <span>N/A</span>}</td>

                {/* Supporting Documents Column */}
                <td>{renderFileButton(uploads?.documents, 'Docs')}</td>

                {/* AI Data (Manuscript) Column */}
                <td>
                    {transcript ? (
                        <ActionButton className="view-file" onClick={() => handlers.onViewTranscript(orderDetails.orderId)}>
                            View Manuscript
                        </ActionButton>
                    ) : <span>Pending AI...</span>}
                </td>

                {/* Video Confirmation Column */}
                <td>{renderFileButton(uploads?.video, 'Video')}</td>

                <td>
                    {activeTab === 'submitted' ? (
                        <>
                            <ActionButton className="edit" onClick={() => handlers.onEdit(orderDetails)} disabled={isAiProcessing || isRegenerating}>{isEditing ? 'Close' : 'Edit Letter'}</ActionButton>
                            <ActionButton className="view" onClick={() => handlers.onViewLetter(orderDetails.orderId)} disabled={isAiProcessing || isRegenerating}>View Letter</ActionButton>
                            <ActionButton className="regenerate" onClick={() => handlers.onRegenerate(orderDetails.orderId)} disabled={isAiProcessing || isRegenerating}>
                                {isRegenerating ? 'Regenerating...' : 'Regenerate Letter'}
                            </ActionButton>
                            <ActionButton className="complete" onClick={() => handlers.onComplete(orderDetails.orderId)} disabled={isCompleting || isAiProcessing || isRegenerating}>
                                {isCompleting ? 'Completing...' : (isAiProcessing ? 'AI Processing...' : (isRegenerating ? 'Please wait...' : 'Complete Order'))}
                            </ActionButton>
                            <ActionButton className="reject" onClick={() => handlers.onReject(orderDetails)} disabled={isCompleting || isAiProcessing || isRegenerating}>
                                Reject
                            </ActionButton>
                        </>
                    ) : (
                        <ActionButton className="reopen" onClick={() => handlers.onReopen(orderDetails.orderId)} disabled={isReopening || isAiProcessing}>
                            {isReopening ? 'Reopening...' : (isAiProcessing ? 'AI Processing...' : 'Reopen Order')}
                        </ActionButton>
                    )}
                </td>
            </tr>
            {isEditing && (
                <tr>
                    <td colSpan="12">
                        <EditorWrapper>
                            <StyledTextArea value={draft} onChange={(e) => handlers.onDraftChange(orderDetails.orderId, e.target.value)} />
                            <div>
                                <ActionButton className="save" onClick={() => handlers.onSave(orderDetails.orderId, draft)}>Save Draft</ActionButton>
                                <ActionButton className="regenerate" onClick={() => handlers.onRegenerate(orderDetails.orderId)}>
                                    Regenerate AI Draft
                                </ActionButton>
                            </div>
                        </EditorWrapper>
                    </td>
                </tr>
            )}
        </>
    );
};

export default OrderRow;